import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { DashCard, DashCardHeader, DashCardBody } from "./v2/DashCard";

interface TopSalesperson {
  id: number;
  name: string;
  sales: number;
  transactions: number;
}

interface TopSalespersonsResponse {
  data: {
    salespersons: TopSalesperson[];
  };
}

const TopSalespersons: React.FC = () => {
  const [period] = useState<"week" | "month" | "year">("month");

  const fetchTopSalespersons = async (): Promise<TopSalespersonsResponse> => {
    const response = await axios.get<TopSalespersonsResponse>(
      `/api/SalesArea/top-salespersons?period=${period}&limit=4`,
    );
    return response.data;
  };

  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["topSalespersons", period],
    queryFn: fetchTopSalespersons,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["topSalespersons", period],
      defaultMessage: "Você não possui permissão para visualizar os melhores vendedores.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const salespersons = !isLoading && !isError && data?.data?.salespersons ? data.data.salespersons : [];
  const maiorVenda = salespersons[0]?.sales || 0;

  const renderPlaceholderRows = () =>
    Array(4)
      .fill(0)
      .map((_, index) => (
        <tr key={index}>
          <td className="dash-skeleton" style={{ height: 14 }} />
          <td className="dash-skeleton" style={{ height: 14 }} />
          <td className="dash-skeleton" style={{ height: 14 }} />
          <td className="dash-skeleton" style={{ height: 14 }} />
        </tr>
      ));

  return (
    <DashCard hoverable={false}>
      <DashCardHeader icon={faStar} variant="warning" title="Top Vendedores" subtitle="Mês atual" />
      <DashCardBody flush>
        <table className="dash-table">
          <thead>
            <tr>
              <th>Vendedor</th>
              <th className="dash-num">Vendas</th>
              <th className="dash-num">Transações</th>
              <th>Desempenho</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderPlaceholderRows()
            ) : isError || salespersons.length === 0 ? (
              <tr>
                <td colSpan={4} className="dash-empty" style={{ padding: "2rem" }}>
                  {isError ? "Erro ao carregar dados" : "Nenhum vendedor encontrado"}
                </td>
              </tr>
            ) : (
              salespersons.map((person) => (
                <tr key={person.id}>
                  <td className="dash-table-primary">{person.name}</td>
                  <td className="dash-num">{person.sales.toLocaleString("pt-BR")}</td>
                  <td className="dash-num">{person.transactions}</td>
                  <td style={{ width: 120 }}>
                    <div style={{ height: 6, borderRadius: 999, background: "var(--dash-border-soft)", overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 999,
                          background: "var(--dash-good)",
                          width: `${maiorVenda > 0 ? (person.sales / maiorVenda) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DashCardBody>
    </DashCard>
  );
};

export default TopSalespersons;
